import { useCallback, useState } from 'react'
import { toast } from 'sonner'
import { listNl } from '../core/format/text'

/** Een los blok dat meegaat met de Opslaan-balk van het beheer. */
export type SavePart = {
  dirty: boolean
  saving: boolean
  /** Wat er nog fout is; dan slaat de balk niets op en zegt waarom. Leeg = in orde. */
  blockedBy: string | null
  /** Hoe het blok heet in een melding, bijv. "De doorverwijzingen". */
  label: string
  save: () => Promise<boolean>
  discard: () => void
}

type Draft = {
  dirty: boolean
  /** `true` = het beheer-concept staat op de server. */
  save: () => Promise<boolean>
  discard: () => void
}

/**
 * Eén Opslaan-balk voor het beheer-concept én losse serverblokken (bijv. de doorverwijzingen).
 * Een blok haakt aan met `registerSave(key, part)` en af met `registerSave(key, null)`.
 */
export function useSaveParts(draft: Draft) {
  // Stabiele referenties: alleen bij een echte wijziging opnieuw.
  const [parts, setParts] = useState<Record<string, SavePart>>({})
  const registerSave = useCallback((key: string, part: SavePart | null) => {
    setParts((prev) => {
      if (part === null) {
        if (!(key in prev)) return prev
        const next = { ...prev }
        delete next[key]
        return next
      }
      return { ...prev, [key]: part }
    })
  }, [])
  const partList = Object.values(parts)

  /**
   * Eerst het beheer-concept, dan elk los blok met wijzigingen. Met fouten: niets, en zeggen waarom.
   * Lukt het concept niet (bijv. 409), dan gaan de losse blokken niet mee. Mislukt een los blok,
   * dan stopt het daar en zegt de melding wat wel en wat niet is opgeslagen.
   */
  const { dirty: draftDirty, save: saveDraft, discard: discardDraft } = draft
  const save = useCallback(async () => {
    const blocked = partList.find((p) => p.dirty && p.blockedBy)
    if (blocked) {
      toast.error('Nog niet opgeslagen', { description: blocked.blockedBy ?? undefined })
      return
    }
    const done: string[] = []
    if (draftDirty) {
      if (!(await saveDraft())) return
      done.push('je overige wijzigingen')
    }
    const pending = partList.filter((p) => p.dirty)
    for (const [i, p] of pending.entries()) {
      if (await p.save()) {
        done.push(p.label.toLowerCase())
        continue
      }
      const notDone = pending.slice(i).map((x) => x.label.toLowerCase())
      toast.error(`${p.label} zijn niet opgeslagen`, {
        description: `${done.length ? `Wel opgeslagen: ${listNl(done)}. ` : 'Er is niets opgeslagen. '}Nog niet opgeslagen: ${listNl(notDone)}. Probeer het opnieuw.`,
      })
      return
    }
  }, [partList, draftDirty, saveDraft])

  const discard = useCallback(() => {
    if (draftDirty) discardDraft()
    for (const p of partList) if (p.dirty) p.discard()
  }, [partList, draftDirty, discardDraft])

  return {
    registerSave,
    dirty: draftDirty || partList.some((p) => p.dirty),
    saving: partList.some((p) => p.saving),
    save,
    discard,
  }
}
